/*
 * The plugin class.
 */
import store from '@/bootstrap/vuex';
import router from '@/bootstrap/router';
import { hasPracticeManagementStanding } from '@/utilities/practice-standing';

const VueUser = {
    install(app, options) {
        // Closure state, not `this`: the methods below are arrow functions,
        // whose `this` is the module scope (undefined in ESM) — reads like
        // `this.email_address` threw for fresh guests and silently broke
        // every caller in the fallback path (e.g. the verification page's
        // no-email redirect guard).
        let emailAddress = '';

        app.config.globalProperties.$user = {
            initialize: () => {
                if (Object.keys(store.state.app.user).length > 0 && store.state.app.user.email_address) {
                    emailAddress = store.state.app.user.email_address;
                }
            },

            setUser: (user) => {
                emailAddress = user.email_address;
                store.commit('app/setUser', user);
                localStorage.setItem('user', JSON.stringify(user));
            },

            reset: () => {
                localStorage.clear();
                sessionStorage.clear();
                store.commit('app/resetUser');
            },

            info: () => {
                return store.state.app.user;
            },

            email: () => {
                if (Object.keys(store.state.app.user).length > 0 && store.state.app.user.email_address) {
                    return store.state.app.user.email_address;
                }

                return emailAddress;
            },

            setUserEmail: (email) => {
                emailAddress = email;
            },

            can: (feature, organisation = null) => {
                let slug = '__error__';

                if (Object.keys(store.state.app.user).length === 0) {
                    return false;
                }

                if (typeof router.currentRoute.value.params.organisation === 'undefined') {
                    if (typeof organisation === 'undefined' || organisation === null) {
                        return false;
                    }

                    slug = organisation;
                } else {
                    slug = router.currentRoute.value.params.organisation;
                }

                if (typeof store.state.organisation[slug]?.permissions[feature] === 'undefined') {
                    console.warn('👋🏽 Did you define [' + feature + '] permission in the database or is this a typo?');
                    //alert('👋🏽 Did you define [' + feature + '] permission in the database or is this a typo?');

                    return false;
                }

                const access = store.state.organisation[slug].permissions[feature];

                if (!access) {
                    console.warn('⚠️ User does not have access to the [' + feature + '] permission.');
                }

                return access;
            },

            hasAccessTo(permissionPrefix, organisation = null) {
                let slug = '__error__';

                if (Object.keys(store.state.app.user).length === 0) {
                    return false;
                }

                if (typeof router.currentRoute.value.params.organisation === 'undefined') {
                    if (typeof organisation === 'undefined' || organisation === null) {
                        return false;
                    }

                    slug = organisation;
                } else {
                    slug = router.currentRoute.value.params.organisation;
                }

                let access = false;

                // Check if any permission starts with the prefix
                access = Object.keys(store.state.organisation[slug]?.permissions).some((permission) => permission.startsWith(permissionPrefix));

                if (!access) {
                    console.warn('⚠️ User does not have access to the [' + permissionPrefix + '] prefix.');
                }

                return access;
            },

            cannot: (feature) => {
                return !Vue.$user.can(feature);
            },

            /*
             * Whether the organisation being acted in is designated an
             * accounting practice (`tenants.is_practice`).
             *
             * Practice management is entitled on every SaaS plan and the Owner
             * role holds every one of its permissions, so a permission check
             * alone says "yes" for every organisation in the product. The
             * designation is the thing that actually decides, and it is what
             * the backend refuses on (GuardsPracticeTenant): showing a
             * navigation entry without consulting it would offer every owner a
             * link to a 403. Read alongside hasAccessTo(), never instead of it
             * — the permissions still decide WHICH of the module's screens a
             * member may open.
             *
             * Absent designation reads as false: an organisation envelope
             * cached by an older build has no `is_practice` key, and the safe
             * reading of "unknown" is to withhold the entry until the next
             * organisation switch refreshes it.
             */
            isPracticeTenant: (organisation = null) => {
                let slug = organisation;

                if (typeof router.currentRoute.value.params.organisation !== 'undefined') {
                    slug = router.currentRoute.value.params.organisation;
                }

                if (typeof slug === 'undefined' || slug === null) {
                    return false;
                }

                return store.state.organisation[slug]?.tenant?.is_practice === true;
            },

            /*
             * Whether the acting member holds practice-management standing in
             * the organisation — any truthy permission under
             * `access.practice-management.`. Since the Client Portal module
             * split, a portal client's one permission lives in
             * `access.client-portal.` and never matches; `isPracticeTenant()`
             * is still equally true for a client acting inside their
             * accountant's tenant, which is why gates pair it with this
             * predicate rather than reading the designation alone. The
             * predicate itself lives in `@/utilities/practice-standing` where
             * it can be tested without this closure.
             */
            hasPracticeManagementStanding: (organisation = null) => {
                let slug = organisation;

                if (typeof router.currentRoute.value.params.organisation !== 'undefined') {
                    slug = router.currentRoute.value.params.organisation;
                }

                if (typeof slug === 'undefined' || slug === null) {
                    return false;
                }

                return hasPracticeManagementStanding(store.state.organisation[slug]?.permissions);
            },

            hasTenantSettings() {
                return store.state.organisation[router.currentRoute.value.params.organisation].settings ? true : false;
            },

            hasFeatureFlag: (featureFlag) => {
                if (Object.keys(store.state.app.user).length === 0) {
                    return false;
                }

                if (typeof store.state.organisation[router.currentRoute.value.params.organisation]?.featureFlags[featureFlag] === 'undefined') {
                    // console.warn('Did you define [' + featureFlag + '] feature flag in the database or is this a typo?');
                    return false;
                }

                return store.state.organisation[router.currentRoute.value.params.organisation].featureFlags[featureFlag];
            },
        };
    },
};

export default VueUser;
